import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, CheckCircle2, MapPin, PackageCheck, Undo2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRefData } from "../context/RefDataContext";
import { romeDate } from "../lib/businessValidation";
import { fmtDate } from "../lib/format";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { StatusPill } from "./StatusBadge";
import EmptyState from "./EmptyState";
import ListSkeleton from "./ListSkeleton";
import VehicleColor from "./VehicleColor";

// The next operation of a rental for this driver: a delivery while it is
// booked, a return once it is out; only when the office assigned it to them.
export function driverOperations(rentals, userId) {
  return (rentals || []).flatMap((r) => {
    if (r.is_archived) return [];
    const dispatch = r.driver_dispatch || {};
    if (["pianificata", "in_consegna"].includes(r.status) && dispatch.delivery?.driver_user_id === userId)
      return [{ rental: r, kind: "consegna", date: r.start_date, site: r.pickup_site_id }];
    if (r.status === "in_corso" && dispatch.return?.driver_user_id === userId)
      return [{ rental: r, kind: "restituzione", date: r.end_date_planned, site: r.return_site_id }];
    return [];
  }).sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
}

function OperationCard({ op, today, siteName }) {
  const delivery = op.kind === "consegna";
  const late = op.date && op.date < today;
  const Icon = delivery ? PackageCheck : Undo2;
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-xl font-bold tracking-tight text-primary">{op.rental.vehicle_plate}</p>
          <VehicleColor vehicleId={op.rental.vehicle_id} showName />
          <p className="truncate text-sm text-muted-foreground">{op.rental.assignee_name}</p>
        </div>
        <StatusPill tone={delivery ? "tone-sky" : "tone-indigo"} icon={Icon}>{delivery ? "Consegna" : "Riconsegna"}</StatusPill>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <span className="inline-flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />{op.date ? fmtDate(op.date) : "Data da definire"}{late && <StatusPill tone="tone-red" className="ml-1">In ritardo</StatusPill>}</span>
        {siteName && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-muted-foreground" aria-hidden="true" />{siteName}</span>}
      </div>
      <Button asChild size="lg" className="mt-4 h-12 w-full text-base">
        <Link to={`/consegne?noleggio=${op.rental.id}`}>{delivery ? "Inizia la consegna" : "Inizia la riconsegna"}<ArrowRight aria-hidden="true" /></Link>
      </Button>
    </Card>
  );
}

export default function DriverToday() {
  const { user } = useAuth();
  const { locations } = useRefData();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let live = true;
    setRows(null); setError("");
    api.get("/assignments").then(({ data }) => { if (live) setRows(Array.isArray(data) ? data : []); })
      .catch((e) => { if (live) setError(apiError(e)); });
    return () => { live = false; };
  }, [retry]);

  const today = romeDate();
  const siteName = (id) => locations.find((site) => site.id === id)?.name;
  const firstName = String(user?.name || "").split(" ")[0];

  if (error) return <Card className="p-5" role="alert"><p>{error}</p><Button className="mt-3" variant="outline" onClick={() => setRetry((n) => n + 1)}>Riprova</Button></Card>;
  if (!rows) return <ListSkeleton rows={3} label="Caricamento delle tue operazioni…" />;
  const ops = driverOperations(rows, user?.id);
  const now = ops.filter((op) => !op.date || op.date <= today);
  const next = ops.filter((op) => op.date && op.date > today);

  return (
    <section aria-labelledby="driver-today-title" className="space-y-4">
      <div>
        <h2 id="driver-today-title" className="font-heading text-2xl font-bold tracking-tight">{firstName ? `Ciao ${firstName}, ecco la tua giornata` : "La tua giornata"}</h2>
        <p className="text-sm text-muted-foreground">{new Intl.DateTimeFormat("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Rome" }).format(new Date())}</p>
      </div>
      {now.length === 0 ? (
        <Card><EmptyState icon={CheckCircle2} title="Niente da fare per oggi" description={next.length ? "Le prossime operazioni sono qui sotto." : "Quando la sede ti assegna una consegna o una riconsegna, la trovi qui."} /></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">{now.map((op) => <OperationCard key={`${op.kind}-${op.rental.id}`} op={op} today={today} siteName={siteName(op.site)} />)}</div>
      )}
      {next.length > 0 && <>
        <h3 className="pt-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Prossimi giorni</h3>
        <div className="grid gap-3 md:grid-cols-2">{next.map((op) => <OperationCard key={`${op.kind}-${op.rental.id}`} op={op} today={today} siteName={siteName(op.site)} />)}</div>
      </>}
    </section>
  );
}
